import React, { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Divider,
  Stack,
  Chip,
} from "@mui/material";
import {
  Security as SecurityIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Headphones as HeadphonesIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", flexDirection: "column" }}>
      {/* LIGHT HEADER BANNER */}
      <Box
        sx={{
          bgcolor: "#f0f7ff",
          borderBottom: "1px solid #e2e8f0",
          py: { xs: 5, md: 6 },
          background: "linear-gradient(135deg, #f0f7ff 0%, #e0f2fe 100%)",
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
            <Chip
              icon={<SecurityIcon sx={{ fontSize: "16px !important", color: "#0284c7 !important" }} />}
              label="PRIVACY & DATA PROTECTION"
              size="small"
              sx={{
                bgcolor: "#ffffff",
                color: "#0284c7",
                fontWeight: 700,
                fontSize: "0.75rem",
                letterSpacing: "0.05em",
                border: "1px solid #bae6fd",
              }}
            />
            <Chip
              icon={<BusinessIcon sx={{ fontSize: "16px !important", color: "#64748b !important" }} />}
              label="Bridgeme Technologies Private Limited"
              size="small"
              sx={{
                bgcolor: "#ffffff",
                color: "#475569",
                fontSize: "0.75rem",
                border: "1px solid #e2e8f0",
              }}
            />
          </Box>

          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: "-0.03em",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "2.75rem" },
              mb: 1.5,
              lineHeight: 1.2,
            }}
          >
            Privacy Policy
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#475569",
              fontSize: { xs: "0.95rem", md: "1.05rem" },
              lineHeight: 1.7,
              maxWidth: 700,
            }}
          >
            This Privacy Policy explains how PulseHR collects, uses, stores, protects, and shares personal information when you use the platform.
          </Typography>
        </Container>
      </Box>

      {/* MAIN CONTENT CONTAINER */}
      <Box sx={{ flex: 1, py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          {/* INTRO */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, fontSize: "1rem", mb: 2 }}>
              Welcome to <strong>PulseHR</strong>, a Human Resource Management platform provided by{" "}
              <strong>Bridgeme Technologies Private Limited</strong> ("Bridgeme Technologies", "Company", "we", "us", or "our").
            </Typography>
            <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, fontSize: "1rem" }}>
              This Privacy Policy explains how PulseHR collects, uses, stores, protects, and shares personal information when you use the platform.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* SECTIONS */}
          <Stack spacing={4.5} sx={{ color: "#334155" }}>
            {/* Scope */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Scope
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                This Privacy Policy applies to users of PulseHR, including:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.75, color: "#334155", lineHeight: 1.7 } }}>
                <li>Customer organizations</li>
                <li>HR administrators</li>
                <li>Employees</li>
              
                <li>Job applicants</li>
              </Box>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mt: 1.5 }}>
                When PulseHR is used by an organization, that organization generally determines how employee and applicant information is collected and used. Bridgeme Technologies processes such information to provide and support PulseHR according to the applicable customer agreement and instructions.
              </Typography>
            </Box>

            <Divider />

          {/* Information We Collect */} <Box>
<Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
Information Collected </Typography>

<Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
Depending on how PulseHR is used, the platform may contain or process the following information: </Typography>

<Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 1, color: "#334155", lineHeight: 1.7 } }}> <li> <strong>Personal Details:</strong> Name, employee ID, date of birth, gender, profile photo, and other employee information. </li> <li> <strong>Contact Details:</strong> Email address, phone number, residential address, and emergency contact information. </li> <li> <strong>Employment Details:</strong> Company, department, designation, work location, reporting manager, joining date, and employment history. </li> <li> <strong>Financial Information:</strong> Bank details, salary information, payslips, reimbursements, and other compensation-related information. </li> <li> <strong>Tax and Statutory Information:</strong> PAN, Aadhaar details, PF/UAN, ESIC information, tax declarations, and related documents. </li> <li> <strong>Attendance and Leave Information:</strong> Attendance records, clock-in and clock-out times, shifts, leave applications, and related information. </li> <li> <strong>Recruitment Information:</strong> Resume, education, previous employment, interview feedback, and offer-related information. </li> <li> <strong>Performance and Training Information:</strong> Goals, appraisals, feedback, ratings, training records, tasks, and timesheets. </li> <li> <strong>Support Information:</strong> Support requests, messages, and documents or attachments submitted through the platform. </li>  </Box>

<Typography variant="body2" sx={{ color: "#64748b", mt: 1.5, fontStyle: "italic" }}>
Some information may be required to provide specific PulseHR features. If required information is not provided, certain services may not be available. </Typography> </Box>

<Divider />

{/* How Information Is Used */} <Box>
<Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
How Information Is Used </Typography>

<Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
Information in PulseHR may be used to: </Typography>

<Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.75, color: "#334155", lineHeight: 1.7 } }}> <li>Provide and operate PulseHR services.</li> <li>Manage employee records and HR activities.</li> <li>Process payroll and compensation information.</li> <li>Manage attendance, shifts, and leave.</li> <li>Support recruitment and onboarding.</li> <li>Manage performance, training, and employee development.</li> <li>Manage projects, tasks, and workforce activities.</li> <li>Maintain system security and audit records.</li> <li>Detect and prevent unauthorized or fraudulent activity.</li> <li>Provide customer support and respond to requests.</li> <li>Send necessary system notifications and administrative communications.</li> <li>Comply with applicable legal and regulatory requirements.</li> </Box> </Box>

<Divider />

{/* How Information Is Shared */} <Box>
<Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
How Information Is Shared </Typography>

<Typography variant="body1" sx={{ color: "#0f172a", lineHeight: 1.8, mb: 1, fontWeight: 600 }}>
Personal information processed through PulseHR is not sold or rented to third parties. </Typography>

<Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
Information may be shared only when necessary to provide and operate PulseHR, including: </Typography>

<Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 1, color: "#334155", lineHeight: 1.7 } }}> <li> <strong>Within the Organization:</strong> Authorized HR personnel, administrators, managers, and other users may access information according to their assigned permissions. </li> <li> <strong>Service Providers:</strong> Trusted providers may process information for services such as hosting, storage, email, messaging, and other technical operations required by PulseHR. </li> <li> <strong>Legal Requirements:</strong> Information may be disclosed when required by applicable law, court orders, government authorities, or regulatory requirements. </li> <li> <strong>Business Changes:</strong> Information may be transferred as part of a merger, acquisition, restructuring, or similar business transaction, subject to appropriate protections. </li> </Box> </Box>


            <Divider />

            {/* Data Security */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Data Security
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                We take reasonable technical and organizational measures to protect information from unauthorized access, misuse, loss, or alteration.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                These measures may include:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.75, color: "#334155", lineHeight: 1.7 } }}>
                <li>Secure data transmission.</li>
                <li>Access controls and role-based permissions.</li>
                <li>Authentication and account security measures.</li>
                <li>Audit logs and activity monitoring.</li>
                <li>Security and system monitoring.</li>
              </Box>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 1.5 }}>
                However, no online system can be guaranteed to be completely secure.
              </Typography>
            </Box>

            <Divider />

            {/* Data Retention */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Data Retention
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                We retain information for as long as necessary to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.75, color: "#334155", lineHeight: 1.7 } }}>
                <li>Provide PulseHR services.</li>
                <li>Maintain customer and employee records.</li>
                <li>Meet contractual requirements.</li>
                <li>Comply with applicable legal and statutory requirements.</li>
                <li>Resolve disputes and maintain appropriate business records.</li>
              </Box>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mt: 1.5 }}>
                When information is no longer required, it may be deleted, archived, or otherwise handled in accordance with applicable requirements and the customer agreement.
              </Typography>
            </Box>

            <Divider />

            {/* Your Rights and Choices */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Your Rights and Choices
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                Depending on applicable law, individuals may have rights regarding their personal information, including the right to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.75, color: "#334155", lineHeight: 1.7 } }}>
                <li>Access their personal information.</li>
                <li>Request correction of inaccurate information.</li>
                <li>Request information about how their data is used.</li>
                <li>Request deletion where legally applicable.</li>
              </Box>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mt: 1.5 }}>
                If you use PulseHR through your organization, you should normally contact your HR department or PulseHR Administrator for requests relating to your employee information.
              </Typography>
            </Box>

            <Divider />

            {/* Changes to This Privacy Policy */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Changes to This Privacy Policy
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8 }}>
                We may update this Privacy Policy from time to time to reflect changes to PulseHR, our practices, or applicable requirements.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mt: 1.5 }}>
                The updated version will be made available through PulseHR or our official website. The Effective Date at the beginning of this policy indicates when the latest version became effective.
              </Typography>
            </Box>

            <Divider />

            {/* Contact Us */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Contact Us
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 2 }}>
                If you have questions or concerns about this Privacy Policy or how information is handled in PulseHR, you may contact <strong>Bridgeme Technologies Private Limited</strong>:
              </Typography>
              <Box sx={{ p: 3, bgcolor: "#ffffff", borderRadius: 2.5, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>
                  Bridgeme Technologies Private Limited
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5 }}>
                  <strong>Product:</strong> PulseHR
                </Typography>

                <Divider sx={{ mb: 2.5 }} />

                <Stack spacing={2.5}>
                  {/* Call Us */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <PhoneIcon sx={{ color: "#ea580c", fontSize: 26, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                        Call Us
                      </Typography>
                      <Typography
                        component="a"
                        href="tel:+919482673071"
                        variant="body2"
                        sx={{ color: "#334155", mt: 0.5, display: "inline-block", textDecoration: "none", fontWeight: 500, "&:hover": { color: "#ea580c" } }}
                      >
                        +91 948 267 3071
                      </Typography>
                    </Box>
                  </Box>

                  {/* Email Us */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <EmailIcon sx={{ color: "#ea580c", fontSize: 26, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                        Email Us
                      </Typography>
                      <Typography
                        component="a"
                        href="mailto:info@bridgemetechnologies.com"
                        variant="body2"
                        sx={{ color: "#334155", mt: 0.5, display: "inline-block", textDecoration: "none", fontWeight: 500, "&:hover": { color: "#ea580c" } }}
                      >
                        info@bridgemetechnologies.com
                      </Typography>
                    </Box>
                  </Box>

                  {/* Customer Support */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <HeadphonesIcon sx={{ color: "#ea580c", fontSize: 26, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                        Customer Support
                      </Typography>
                      <Typography
                        component="a"
                        href="mailto:info@bridgemetechnologies.com"
                        variant="body2"
                        sx={{ color: "#334155", mt: 0.5, display: "inline-block", textDecoration: "none", fontWeight: 500, "&:hover": { color: "#ea580c" } }}
                      >
                        info@bridgemetechnologies.com
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* FOOTER */}
      <Box sx={{ bgcolor: "#ffffff", borderTop: "1px solid #e2e8f0", py: 3, mt: "auto" }}>
        <Container maxWidth="md">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              © {new Date().getFullYear()} PulseHR · Bridgeme Technologies Private Limited
            </Typography>
            <Stack direction="row" spacing={3}>
              <Link
                to="/privacy"
                style={{ textDecoration: "none", color: "#0284c7", fontSize: "0.875rem", fontWeight: 600 }}
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                style={{ textDecoration: "none", color: "#64748b", fontSize: "0.875rem", fontWeight: 500 }}
              >
                Terms of Use
              </Link>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
